import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonProps,
} from "@mui/material";
import ChatSettingsTooltip from "src/reusable/components/ChatSettingsTooltip";
import {
  RESPONSE_MODES,
  ResponseMode,
} from "src/reusable/Chat/types/chatTypes";
import { useChatStore } from "src/views/assistant/stores/ChatStore";

/** Composer copy per mode; a new mode is one more row here. */
const MODE_COPY: Record<ResponseMode, { label: string; tooltip: string }> = {
  thorough: {
    label: "Thorough",
    tooltip:
      "Thorough: full research — sub-agents, wide searches, detailed answers.",
  },
  quick: {
    label: "Quick",
    tooltip: "Quick: lighter reasoning, faster responses.",
  },
};

const LOCKED_TOOLTIP =
  "Response mode is fixed once a conversation starts. Start a new chat to change it.";

/**
 * A group button carrying its own tooltip, so hovering explains the hovered
 * mode rather than the selected one. Safe to wrap: ToggleButtonGroup hands
 * selection and size to its buttons through context, not cloned props.
 */
const ModeButton = ({
  tooltip,
  ...props
}: ToggleButtonProps & { tooltip: string }) => (
  <ChatSettingsTooltip title={tooltip} placement="top" enterDelay={200} arrow>
    <ToggleButton {...props} />
  </ChatSettingsTooltip>
);

/**
 * Thorough / Quick answer-style toggle for the AtomAssist composer.
 *
 * Purely store-driven: the value rides to the backend as `response_mode` on
 * /chat/start (see ChatStore.responseMode). Editable only until the first
 * message of a conversation is submitted; locked for that dialogue after.
 * Defaults to Thorough. Buttons come from RESPONSE_MODES.
 */
const ChatResponseModeToggle = () => {
  const responseMode = useChatStore((state) => state.responseMode);
  const locked = useChatStore((state) => state.responseModeLocked);
  const setResponseMode = useChatStore((state) => state.setResponseMode);

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    next: ResponseMode | null,
  ) => {
    // Exclusive groups emit null when the active button is clicked again;
    // one mode is always selected, so ignore that.
    if (next !== null) {
      setResponseMode(next);
    }
  };

  const group = (
    <ToggleButtonGroup
      size="small"
      color="primary"
      exclusive
      disabled={locked}
      value={responseMode}
      onChange={handleChange}
      aria-label="Response mode"
      data-testid="aa-response-mode-toggle"
      sx={{
        "& .MuiToggleButton-root": {
          textTransform: "none",
          px: 1.25,
          py: 0.25,
          lineHeight: 1.5,
        },
      }}
    >
      {RESPONSE_MODES.map((mode) => (
        <ModeButton
          key={mode}
          value={mode}
          tooltip={locked ? "" : MODE_COPY[mode].tooltip}
          aria-label={`${MODE_COPY[mode].label} responses`}
          data-attr={`assistant:toggle:response-mode-${mode}`}
        >
          {MODE_COPY[mode].label}
        </ModeButton>
      ))}
    </ToggleButtonGroup>
  );

  if (!locked) {
    return group;
  }
  // Disabled buttons swallow pointer events, so the lock is explained from a
  // span around the whole group, which still receives them.
  return (
    <ChatSettingsTooltip
      title={LOCKED_TOOLTIP}
      placement="top"
      enterDelay={200}
      arrow
    >
      <span>{group}</span>
    </ChatSettingsTooltip>
  );
};

export default ChatResponseModeToggle;
